/**
 * v2.29 — Jump to Rail / Genre dropdown.
 *
 * Grouped, accordion-style picker that lets users skip the long home feed
 * and anchor to any rail. Five categories matching user-curated taxonomy.
 *
 * Each item has either:
 *   - `tid`: a data-testid on the home page — smooth-scrolls to it
 *   - `href`: an external route — navigates with react-router
 *
 * Lazy-mount-aware: rails below the fold get an intermediate scroll to
 * the LazyMount placeholder first, then refine to the real rail once it
 * has hydrated.
 */
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ListTree, ChevronDown, ChevronRight } from "lucide-react";

// All items verified against actual data-testids on Home.jsx + component files.
// Items with `href` only (no tid) route to dedicated pages like /top/movie/trending.
const GROUPS = [
  {
    id: "for-you",
    label: "For You",
    sublabel: "personalised & fresh",
    items: [
      { label: "For You ✨", tid: "row-for-you" },
      { label: "Recommended For You", tid: "row-because-you-clicked" },
    ],
  },
  {
    id: "trending",
    label: "Trending & Charts",
    sublabel: "India, global, rated & curated",
    items: [
      // v2.95 — Rail restructure: removed Recently Released India + Top 50
      // Series/Movies Trending Now. Added New on OTT (scope-tabbed) and
      // Now in Theatres (scope-tabbed). Categories dropdown updated to
      // match the new Home rail order so users don't jump to a removed rail.
      { label: "🎬 New on OTT", tid: "section-new-on-ott" },
      { label: "🍿 Now in Theatres", tid: "section-now-in-theatres" },
      { label: "Coming Soon", tid: "home-anticipated" },
      { label: "Trending in India", tid: "row-trending-india" },
      { label: "Trending Now (Global)", tid: "row-trending-global" },
      { label: "Editor's Picks", tid: "row-editor-picks" },
      { label: "Top Rated · IMDb Masterpieces", tid: "row-imdb-masterpieces" },
      { label: "Watch On (OTT)", tid: "home-watch-on" },
      { label: "Weekend Binge", tid: "row-weekend-binge" },
      { label: "Hidden Gems", tid: "row-hidden-gems" },
      { label: "Top 50 Movies · All Time", tid: "row-top-movies-alltime" },
      { label: "Top 50 Series · All Time", tid: "row-top-series-alltime" },
    ],
  },
  {
    id: "indian",
    label: "Indian Content",
    sublabel: "cinema, classics & originals",
    items: [
      { label: "Indian Modern Classics", tid: "row-indian-modern-classics" },
      { label: "Indian Classics", tid: "home-classics-rail" },
      { label: "Top 50 Movies · India", href: "/top/movies/all_time?language=hi" },
      { label: "Top 50 Series · India", href: "/top/tv/all_time?language=hi" },
    ],
  },
  {
    id: "genres",
    label: "Genres & Moods",
    sublabel: "action, drama, emotion & more",
    items: [
      { label: "High-Octane Action", tid: "row-high-octane-action" },
      { label: "Survival Thrillers", tid: "row-survival-thrillers" },
      { label: "Mind-Bending Thrillers", tid: "row-mind-bending-thrillers" },
      { label: "Intelligent Sci-Fi", tid: "row-intelligent-scifi" },
      { label: "Emotional Masterpieces", tid: "row-emotional-masterpieces" },
      { label: "Motivational Films", tid: "row-motivational-films" },
      { label: "Corporate & Startup Stories", tid: "row-corporate-startup-stories" },
      { label: "All-Time Hit Series", tid: "row-all-time-hit-series" },
      { label: "Comedy Hits", tid: "row-comedy-hits" },
      { label: "Family Favourites", tid: "row-family-favourites" },
      { label: "Horror Hits", tid: "row-horror-hits" },
      // v2.48 — Stand-Up & Animation rails (with inline pill filters).
      { label: "Stand-Up Comedy", tid: "row-stand-up-comedy" },
      { label: "Animation", tid: "row-animation" },
    ],
  },
  {
    id: "fandoms",
    label: "Fandoms, Docs & Live",
    sublabel: "franchises, lean-back & live TV",
    items: [
      { label: "Marvel Magic", tid: "row-marvel-magic" },
      { label: "K-Drama Obsession", tid: "row-k-drama-obsession" },
      { label: "Ghibli Magic", tid: "row-ghibli-magic" },
      { label: "Fantasy Worlds", tid: "row-fantasy-worlds" },
      { label: "Cartoon Network Classics", tid: "row-cartoon-network-classics" },
      { label: "Documentaries", tid: "row-all-documentaries" },
      { label: "Mind, Music & Motivation", tid: "row-mind-music-motivation-wrap" },
      { label: "Featured Creators", tid: "home-creators-hero" },
      { label: "Live TV", tid: "home-live-tv" },
    ],
  },
];

// v2.30 — Conditional rails: these only render based on data/auth state.
// We filter these out of the dropdown when their target testid isn't on
// the page. Below-the-fold rails (LazyMount) are NOT filtered — they
// hydrate as the user scrolls down via the picker.
const CONDITIONAL_TIDS = new Set([
  "row-for-you",
  "row-because-you-clicked",
]);

export default function JumpToRail() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [presentTids, setPresentTids] = useState(() => new Set());
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  // v2.30 — Compute the subset of conditional rails currently in the DOM.
  // Recomputed each time the dropdown opens. Only conditional entries are
  // filtered; LazyMount placeholders stay so users can navigate to a rail
  // that hasn't mounted yet (the scroll triggers the mount).
  const refreshPresent = () => {
    const next = new Set();
    GROUPS.forEach((g) => {
      g.items.forEach((it) => {
        if (it.href || !it.tid) {
          next.add(it.tid || it.href);
          return;
        }
        if (!CONDITIONAL_TIDS.has(it.tid)) {
          // Non-conditional: assume present (lazy mount handles deferred ones).
          next.add(it.tid);
          return;
        }
        if (document.querySelector(`[data-testid='${it.tid}']`)) {
          next.add(it.tid);
        }
      });
    });
    setPresentTids(next);
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    refreshPresent();
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // v2.33 — single-accordion behaviour. Opening a new group collapses all
  // others (user preference: "Jump to should have only 1 dropdown category
  // open at a time"). Clicking the currently-open group closes it.
  const toggleGroup = (gid) => setExpanded((e) => (e[gid] ? {} : { [gid]: true }));

  const handlePick = (item) => {
    setOpen(false);
    if (item.href) {
      navigate(item.href);
      return;
    }
    // Anchor scroll: try real testid first, fall back to lazy-mount placeholder.
    const real = document.querySelector(`[data-testid='${item.tid}']`);
    if (real) {
      const y = real.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
      return;
    }
    const lazyId = item.tid.startsWith("row-")
      ? `lazy-${item.tid.slice(4)}`
      : item.tid.startsWith("home-")
      ? `lazy-${item.tid.slice(5)}`
      : `lazy-${item.tid}`;
    const lazy = document.querySelector(`[data-testid='${lazyId}']`);
    if (lazy) {
      const y = lazy.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
      // Refine after hydration.
      setTimeout(() => {
        const r2 = document.querySelector(`[data-testid='${item.tid}']`);
        if (r2) {
          const y2 = r2.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: y2, behavior: "smooth" });
        }
      }, 800);
    }
  };

  return (
    <div className="px-6 lg:px-10 mt-4" data-testid="jump-to-rail">
      <div className="max-w-[1440px] mx-auto relative" ref={wrapRef}>
        <button
          onClick={() => setOpen((s) => !s)}
          data-testid="jump-to-rail-toggle"
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-bold tracking-wide border border-white/15 bg-white/[0.04] hover:bg-white/[0.10] hover:border-white/25 transition text-white/85"
        >
          <ListTree className="w-3.5 h-3.5 text-amber-300" />
          <span>Jump to · By Mood &amp; Genre</span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div
            className="absolute left-0 z-30 mt-2 w-[320px] sm:w-[420px] max-h-[60vh] overflow-y-auto rounded-xl border border-white/15 bg-[#0E0E0E]/95 backdrop-blur-md shadow-2xl py-1"
            data-testid="jump-to-rail-list"
          >
            {GROUPS.map((g) => {
              const isOpen = !!expanded[g.id];
              // v2.30 — Hide items that have no DOM target. Re-runs every
              // time the dropdown opens via the effect above.
              const visibleItems = g.items.filter((it) => {
                if (it.href) return true;
                if (!it.tid) return false;
                return presentTids.has(it.tid);
              });
              if (visibleItems.length === 0) return null;
              return (
                <div key={g.id} className="border-b border-white/[0.04] last:border-b-0">
                  <button
                    onClick={() => toggleGroup(g.id)}
                    data-testid={`jump-group-${g.id}`}
                    className="w-full text-left px-4 py-2.5 flex items-center justify-between hover:bg-white/[0.04] transition"
                  >
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-white/95 tracking-wide">
                        {g.label}
                      </span>
                      <span className="text-[10px] text-white/45 mt-0.5">
                        {g.sublabel} · {visibleItems.length} rows
                      </span>
                    </div>
                    <ChevronRight
                      className={`w-3.5 h-3.5 text-white/40 shrink-0 transition-transform ${
                        isOpen ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <ul className="pb-1.5">
                      {visibleItems.map((it, i) => (
                        <li key={`${g.id}-${i}`}>
                          <button
                            onClick={() => handlePick(it)}
                            data-testid={`jump-item-${g.id}-${i}`}
                            className="w-full text-left pl-7 pr-4 py-1.5 text-[12px] text-white/75 hover:bg-white/[0.06] hover:text-white transition"
                          >
                            {it.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
